import { Stack, Typography, useTheme } from '@mui/material'
import { dayToJSDate, formatAmPm, formatDate, getDOWFromStr } from '@shared/dates'
import { i18WithDefault as t } from '@shared/locales'
import { displayHoursInPeriodRestrictions, isSelectedDateInPeriodRestrictionsRelation } from '@shared/restrictions'
import type { BookingConfigsClientDto, OrderClientDto, PaymentSettingAdminDto } from 'service-api/src/types/api'
import { FeeStructure } from './fee_structure'

export interface LateDepartureDetailsProps {
  endDate?: string
  endTime?: string | null
  order: OrderClientDto
  bookingConfig: BookingConfigsClientDto
  header?: string
}

export const LateDepartureDetails = ({
  endDate,
  endTime,
  order,
  bookingConfig,
  header: customHeader,
}: LateDepartureDetailsProps) => {
  const { locationBookingConf, checkOutHour, operationHour } = bookingConfig
  const theme = useTheme()
  const checkOutDay = endDate && getDOWFromStr(endDate)

  const lateDeparture = (
    order?.data as Record<string, PaymentSettingAdminDto[]> | undefined
  )?.surchargeSettings?.filter(
    (fee: PaymentSettingAdminDto) =>
      fee?.after &&
      checkOutDay &&
      isSelectedDateInPeriodRestrictionsRelation(fee.restrictionRelations!, checkOutDay, endDate) &&
      (locationBookingConf?.surchargeStrategy === 'ENFORCED' || locationBookingConf?.surchargeStrategy === 'DURATION')
  ) as PaymentSettingAdminDto[]
  const isCheckOutEnabled =
    checkOutHour &&
    checkOutDay &&
    !!displayHoursInPeriodRestrictions(checkOutHour, checkOutDay, endDate) &&
    (locationBookingConf?.surchargeStrategy === 'SUGGESTED' || locationBookingConf?.surchargeStrategy === 'ENFORCED')

  const defaultLateDepartureHeader = `${t('surchargePolicy.departure')}: ${formatDate(dayToJSDate(endDate))} ${
    endTime ? formatAmPm(endTime) : ''
  }`

  return (
    <Stack sx={{ border: `1px solid ${theme.palette.divider}`, p: 2, borderRadius: 1 }}>
      {endDate && (
        <Typography variant="body1" style={{ fontWeight: 700 }}>
          {customHeader || defaultLateDepartureHeader}
        </Typography>
      )}
      {checkOutDay && operationHour && (
        <Typography variant="caption" style={{ fontStyle: 'italic', color: theme.palette.text.secondaryShade60 }}>
          {t('surchargePolicy.open')}: {displayHoursInPeriodRestrictions(operationHour, checkOutDay, endDate)}
        </Typography>
      )}
      {isCheckOutEnabled && checkOutDay && (
        <Typography variant="body2" mb={1}>
          <strong>{t('surchargePolicy.checkOutHours')}:</strong>{' '}
          {displayHoursInPeriodRestrictions(checkOutHour!, checkOutDay, endDate)}
        </Typography>
      )}
      {!!lateDeparture?.length && <FeeStructure type="lateDeparture" fees={lateDeparture} />}
    </Stack>
  )
}
